import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  AlertTriangle,
  Bot,
  CheckCircle2,
  FolderTree,
  Hash,
  Link2,
  Loader,
  Megaphone,
  RefreshCw,
  ShieldCheck,
  UserPlus,
  UserX,
  XCircle,
} from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { EmptyState } from '../ui/EmptyState';
import { SectionHeader } from '../ui/SectionHeader';
import { StatTile } from '../ui/StatTile';
import { SkeletonLoader } from '../SkeletonLoader';
import { PartnerAvatar } from './PartnerAvatar';
import { PartnerNoticeBar } from './PartnerNoticeBar';
import { PartnerTierBadge } from './PartnerPhaseBadge';
import { PARTNER_TIERS, type PartnerDiscordHealth, type PartnerDiscordRoleHealth, type PartnerSummary, type PartnerTier } from '../../types/partners';
import { cn } from '../../utils/cn';
import { discordName, partnerPagePath } from '../../utils/partnerLinks';
import { phaseLabel, tierLabel } from '../../utils/partnerStatus';
import { describeSyncOutcome, type PartnerNotice } from '../../utils/partnerSync';

interface PartnerDiscordAdminPanelProps {
  canEdit: boolean;
}

const RoleCard = ({ tier, role }: { tier: PartnerTier; role: PartnerDiscordRoleHealth }) => {
  const healthy = role.exists && role.assignable && role.problems.length === 0;
  return (
    <div className="subcard p-3 space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-white truncate">{role.name ?? tierLabel(tier).label}</p>
          <p className="text-[11px] text-glass-500">
            {tierLabel(tier).label} role{role.id ? <> · <span className="font-mono">{role.id}</span></> : null}
          </p>
        </div>
        <span className={cn('badge badge-dot', healthy ? 'badge-success' : 'badge-danger')}>{healthy ? 'Ready' : 'Needs attention'}</span>
      </div>
      <p className="text-xs text-glass-400 tabular-nums">
        {role.holders} {role.holders === 1 ? 'person holds' : 'people hold'} it.
        {!role.exists && ' The role does not exist in the server.'}
        {role.exists && !role.assignable && ' Mr. Roboto cannot hand it out: its own role must sit above it.'}
      </p>
      {role.problems.length > 0 && (
        <ul className="text-xs text-red-400 list-disc pl-4 space-y-0.5">
          {role.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Partner Discord at a glance, for the whole programme: whether Mr. Roboto's
 * partner sync is on, whether its roles and categories are usable, which
 * partner channels are not linked to anyone, and who holds a partner role
 * without being on any team.
 */
export const PartnerDiscordAdminPanel = ({ canEdit }: PartnerDiscordAdminPanelProps) => {
  const [health, setHealth] = useState<PartnerDiscordHealth | null>(null);
  const [partners, setPartners] = useState<PartnerSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partnersError, setPartnersError] = useState<string | null>(null);
  const [notice, setNotice] = useState<PartnerNotice | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [linkTo, setLinkTo] = useState<Record<string, string>>({});
  const [strayTo, setStrayTo] = useState<Record<string, { creatorId: string; tier: PartnerTier }>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const [h, p] = await Promise.allSettled([partnersApi.discordHealth(), partnersApi.list()]);
    if (h.status === 'fulfilled') {
      setHealth(h.value);
      setError(null);
    } else {
      setError(h.reason instanceof Error ? h.reason.message : 'Mr. Roboto cannot be reached right now.');
    }
    if (p.status === 'fulfilled') {
      setPartners(p.value);
      setPartnersError(null);
    } else {
      setPartnersError(p.reason instanceof Error ? p.reason.message : 'The partner list could not be loaded.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const sortedPartners = useMemo(() => [...(partners ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [partners]);
  const withoutChannel = useMemo(() => sortedPartners.filter((p) => !p.channel.id), [sortedPartners]);

  const link = async (channel: PartnerDiscordHealth['unlinked_channels'][number]) => {
    const creatorId = linkTo[channel.id];
    const partner = partners?.find((p) => p.creator_id === creatorId);
    if (!creatorId || !partner) return;
    setBusy(`link:${channel.id}`);
    try {
      const res = await partnersApi.linkChannel(creatorId, channel.id);
      const outcome = describeSyncOutcome(res.sync, `Linked #${channel.name} to ${partner.name}.`, res.discord.managed);
      setNotice({
        ...outcome,
        text: res.discord.managed ? outcome.text : `${outcome.text} Next, preview it on the partner page, import the people who already have access, and turn management on.`,
        link: { to: partnerPagePath(creatorId, 'discord'), label: `Open ${partner.name}’s Discord section` },
      });
      await load();
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'The channel could not be linked.' });
    } finally {
      setBusy(null);
    }
  };

  const addStray = async (stray: PartnerDiscordHealth['strays'][number]) => {
    const choice = strayTo[stray.discord_id];
    const partner = partners?.find((p) => p.creator_id === choice?.creatorId);
    if (!choice || !partner) return;
    setBusy(`stray:${stray.discord_id}`);
    try {
      const res = await partnersApi.addMember(choice.creatorId, { discord_id: stray.discord_id, tier: choice.tier });
      setNotice({
        ...describeSyncOutcome(res.sync, `Added ${discordName(stray, stray.discord_id)} to ${partner.name}’s team as a ${tierLabel(choice.tier).label}.`, partner.managed),
        link: { to: partnerPagePath(choice.creatorId, 'team'), label: `Open ${partner.name}’s team` },
      });
      await load();
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof Error ? e.message : 'They could not be added.' });
    } finally {
      setBusy(null);
    }
  };

  if (loading && !health) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLoader key={i} className="h-20" />
          ))}
        </div>
        <div className="glass-card p-4 space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <SkeletonLoader key={i} className="h-10" />
          ))}
        </div>
      </div>
    );
  }

  if (!health) {
    return (
      <EmptyState
        tone="danger"
        icon={Bot}
        title="Mr. Roboto cannot be reached right now"
        description={`${error ?? 'Its partner health could not be read.'} Partner teams can still be edited; Discord catches up once it is back.`}
        action={
          <button type="button" className="glass-button" onClick={() => void load()}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        }
      />
    );
  }

  const { roles, loop } = health;

  return (
    <div className="space-y-4">
      <PartnerNoticeBar notice={notice} onDismiss={() => setNotice(null)} />

      {error && (
        <div className="alert alert-warning" role="status">
          <AlertTriangle className="w-4 h-4" />
          <span className="flex-1">Showing the last answer. {error}</span>
        </div>
      )}
      {!health.enabled && (
        <div className="alert alert-warning">
          <AlertTriangle className="w-4 h-4" />
          <span>
            Partner sync is switched off in Mr. Roboto’s config (ModpackPartners.ENABLED). Previews still work, but nothing changes in Discord until it
            is switched on.
          </span>
        </div>
      )}
      {health.enabled && !health.remove_roles && (
        <div className="alert alert-info">
          <ShieldCheck className="w-4 h-4" />
          <span>
            Setup mode: Mr. Roboto hands out partner roles but holds back every role removal until REMOVE_ROLES is on. Each partner’s Discord section
            shows what is held.
          </span>
        </div>
      )}
      {loop.consecutive_failures > 0 && (
        <div className="alert alert-danger">
          <XCircle className="w-4 h-4" />
          <span>
            The last {loop.consecutive_failures} {loop.consecutive_failures === 1 ? 'pass' : 'passes'} failed
            {loop.last_result ? `: ${loop.last_result}` : '.'} Changes saved in Argus wait until a pass succeeds.
          </span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Partnered Developers" value={roles.developer.holders} icon={ShieldCheck} tone="purple" hint="hold the developer role" />
        <StatTile label="Contributors" value={roles.contributor.holders} icon={ShieldCheck} tone="info" hint="hold the contributor role" />
        <StatTile
          label="Unlinked channels"
          value={health.unlinked_channels.length}
          icon={Hash}
          tone={health.unlinked_channels.length ? 'warning' : 'neutral'}
          hint="in partner categories"
        />
        <StatTile label="Strays" value={health.strays.length} icon={UserX} tone={health.strays.length ? 'warning' : 'neutral'} hint="role holders on no team" />
      </div>

      <div className="glass-card p-4 md:p-5 space-y-4">
        <SectionHeader
          title="Mr. Roboto’s setup"
          icon={Bot}
          actions={
            <button type="button" className="glass-button glass-button-sm" onClick={() => void load()} disabled={loading}>
              <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
              Refresh
            </button>
          }
        />
        <div className="grid gap-3 md:grid-cols-2">
          <RoleCard tier="developer" role={roles.developer} />
          <RoleCard tier="contributor" role={roles.contributor} />
        </div>

        <div>
          <p className="eyebrow mb-2 flex items-center gap-1.5">
            <FolderTree className="w-3.5 h-3.5" /> Categories
          </p>
          {health.categories.length === 0 ? (
            <p className="text-sm text-glass-400">No partner categories are configured (CATEGORY_IDS), so Mr. Roboto has nowhere to put channels.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th">Category</th>
                    <th className="th">Kind</th>
                    <th className="th text-right">Channels</th>
                    <th className="th text-right">Room</th>
                    <th className="th">Problems</th>
                  </tr>
                </thead>
                <tbody>
                  {health.categories.map((cat) => (
                    <tr key={cat.id}>
                      <td className="td">
                        <span className="text-white">{cat.name ?? 'Unknown category'}</span>
                        <span className="block text-[11px] font-mono text-glass-500">{cat.id}</span>
                      </td>
                      <td className="td">
                        <span className={cn('badge', cat.kind === 'archive' ? 'badge-neutral' : 'badge-accent')}>{cat.kind === 'archive' ? 'Archive (staff only)' : 'Partner channels'}</span>
                      </td>
                      <td className="td text-right tabular-nums">{cat.channels}</td>
                      <td className={cn('td text-right tabular-nums', cat.room < 5 && 'text-amber-300')}>{cat.room}</td>
                      <td className="td text-xs">
                        {!cat.exists ? (
                          <span className="text-red-400">The category does not exist.</span>
                        ) : cat.missing_permissions.length ? (
                          <span className="text-red-400">Mr. Roboto is missing: {cat.missing_permissions.join(', ')}</span>
                        ) : (
                          <span className="text-green-400 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Fine
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-glass-500 mt-1.5">Discord allows 50 channels per category. When one fills up, Mr. Roboto uses the next, and makes a new overflow category if none has room.</p>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="subcard p-3 space-y-1">
            <p className="eyebrow flex items-center gap-1.5">
              <Megaphone className="w-3.5 h-3.5" /> Staff feed
            </p>
            <p className="text-sm text-white">{health.feed.name ? `#${health.feed.name}` : 'Not configured'}</p>
            <p className={cn('text-xs', health.feed.ok ? 'text-glass-400' : 'text-red-400')}>
              {health.feed.ok ? 'Update reports are copied here for staff.' : health.feed.problem ?? 'Mr. Roboto cannot post here.'}
            </p>
          </div>
          <div className="subcard p-3 space-y-1">
            <p className="eyebrow flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Background passes
            </p>
            <p className="text-sm text-white">
              {loop.last_full_at ? `Last full pass ${formatDistanceToNow(new Date(loop.last_full_at), { addSuffix: true })}` : 'No full pass yet'}
            </p>
            <p className="text-xs text-glass-400">
              {loop.last_result ? `Result: ${loop.last_result}. ` : ''}
              {loop.snapshot_age_s !== null ? `Its copy of the teams is ${Math.round(loop.snapshot_age_s / 60)} min old. ` : ''}
              Mr. Roboto checks every partner every 10 minutes and whenever someone joins.
            </p>
          </div>
        </div>
      </div>

      <div className="glass-card p-4 md:p-5 space-y-3">
        <SectionHeader title="Channels no partner has yet" icon={Hash} count={health.unlinked_channels.length} />
        <p className="text-sm text-glass-400">
          Text channels in the partner and archive categories that are not linked to a partner, usually the hand-made channels from before Argus kept
          the teams. Link each to its partner, then import the people who already have access from that partner’s Discord section.
        </p>
        {partnersError && (
          <div className="alert alert-warning">
            <AlertTriangle className="w-4 h-4" />
            <span>{partnersError} Channels cannot be linked from here until it loads.</span>
          </div>
        )}
        {health.unlinked_channels.length === 0 ? (
          <EmptyState bare compact icon={CheckCircle2} title="Every partner channel is linked" />
        ) : (
          <ul className="divide-y divide-glass-200/10">
            {health.unlinked_channels.map((channel) => (
              <li key={channel.id} className="py-2.5 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[12rem]">
                  <p className="text-sm text-white font-mono">#{channel.name}</p>
                  <p className="text-[11px] text-glass-500">
                    {channel.category_name ?? 'Unknown category'} · <span className="font-mono">{channel.id}</span>
                  </p>
                </div>
                {canEdit && partners && (
                  <div className="flex flex-wrap items-center gap-2">
                    <select
                      className="glass-select min-h-[32px] max-w-[16rem]"
                      value={linkTo[channel.id] ?? ''}
                      onChange={(e) => setLinkTo((prev) => ({ ...prev, [channel.id]: e.target.value }))}
                      aria-label={`Which partner #${channel.name} belongs to`}
                    >
                      <option value="">Which partner?</option>
                      {withoutChannel.map((p) => (
                        <option key={p.creator_id} value={p.creator_id}>
                          {p.name} ({phaseLabel(p.phase).label})
                        </option>
                      ))}
                    </select>
                    <button type="button" className="glass-button glass-button-sm" disabled={!linkTo[channel.id] || busy !== null} onClick={() => void link(channel)}>
                      {busy === `link:${channel.id}` ? <Loader className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                      Link
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="glass-card p-4 md:p-5 space-y-3">
        <SectionHeader title="Partner roles held by people on no team" icon={UserX} count={health.strays.length} />
        <p className="text-sm text-glass-400">
          Mr. Roboto never removes these roles by itself, because Argus has no record of why they were given. Add each person to the team they belong
          to, or take the role away by hand in Discord if they should not have it.
        </p>
        {health.strays.length === 0 ? (
          <EmptyState bare compact icon={CheckCircle2} title="Everyone with a partner role is on a team" />
        ) : (
          <ul className="divide-y divide-glass-200/10">
            {health.strays.map((stray) => {
              const name = discordName(stray, stray.discord_id);
              const choice = strayTo[stray.discord_id] ?? { creatorId: '', tier: stray.roles.includes('developer') ? 'developer' : 'contributor' };
              return (
                <li key={stray.discord_id} className="py-2.5 flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2.5 flex-1 min-w-[12rem]">
                    <PartnerAvatar avatarUrl={null} name={name} size="md" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white truncate">{name}</p>
                      <p className="text-[11px] font-mono text-glass-500 truncate">
                        {stray.username ? `@${stray.username} · ` : ''}
                        {stray.discord_id}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {stray.roles.map((role) => (
                        <PartnerTierBadge key={role} tier={role} />
                      ))}
                    </div>
                  </div>
                  {canEdit && partners && (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className="glass-select min-h-[32px] max-w-[14rem]"
                        value={choice.creatorId}
                        onChange={(e) => setStrayTo((prev) => ({ ...prev, [stray.discord_id]: { ...choice, creatorId: e.target.value } }))}
                        aria-label={`Team for ${name}`}
                      >
                        <option value="">Which team?</option>
                        {sortedPartners.map((p) => (
                          <option key={p.creator_id} value={p.creator_id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                      <select
                        className="glass-select min-h-[32px]"
                        value={choice.tier}
                        onChange={(e) => setStrayTo((prev) => ({ ...prev, [stray.discord_id]: { ...choice, tier: e.target.value as PartnerTier } }))}
                        aria-label={`Tier for ${name}`}
                      >
                        {PARTNER_TIERS.map((tier) => (
                          <option key={tier} value={tier}>
                            {tierLabel(tier).label}
                          </option>
                        ))}
                      </select>
                      <button type="button" className="glass-button glass-button-sm" disabled={!choice.creatorId || busy !== null} onClick={() => void addStray(stray)}>
                        {busy === `stray:${stray.discord_id}` ? <Loader className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        Add to team
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {partners && partners.some((p) => p.managed && p.channel.error) && (
        <div className="glass-card p-4 md:p-5 space-y-3">
          <SectionHeader title="Partners with a Discord problem" icon={AlertCircle} />
          <ul className="divide-y divide-glass-200/10">
            {sortedPartners
              .filter((p) => p.managed && p.channel.error)
              .map((p) => (
                <li key={p.creator_id} className="py-2.5 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={partnerPagePath(p.creator_id, 'discord')} className="link text-sm font-medium">
                      {p.name}
                    </Link>
                    <p className="text-xs text-red-400">{p.channel.error}</p>
                  </div>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
};
